import { useNotificationsStore } from '@/stores/notification/notification';

import { useStapConfigStore } from '@/stores/stap-config/stap-config';
import leadAanvraagApi from '@/api/lead-aanvraag.api';
import { savingPromise } from '@/stores/lead-aanvraag/functions//sequential-save-stap';
import { updateCurrentSubflowState } from '@/stores/lead-aanvraag/functions/update-current-subflow-state';
import { useLeadAanvraagStore } from '@/stores/lead-aanvraag/lead-aanvraag';
import { useFlowUrls } from '@/use/use-flow-urls';
import { Base64 } from 'js-base64';
import router from '@/router';
import { computed } from 'vue';
import { useRouteExtensions } from '@/use/use-route-extensions';
import { useFlowNavigation } from '@/use/use-flow-navigation';
import { useQueryClient } from '@tanstack/vue-query';
import { useAanbodExtensions } from '@/use/aanbod-definitie/use-aanbod-extensions';
import { useAanbodDefinitieByVersionQueryInFunction } from '@/use/aanbod-definitie/use-aanbod-definitie-by-version-query';

export const useNext = () => {
  const flowNavigation = useFlowNavigation();
  const queryClient = useQueryClient();
  const { currentPageConfig, pageHasMultipleStappen } = useAanbodExtensions();

  async function next(noLoading, subflowState, subflowOverride, aanbodOverride) {
    const leadAanvraagStore = useLeadAanvraagStore();
    try {
      const notificationsStore = useNotificationsStore();
      const flowUrls = useFlowUrls();

      if (leadAanvraagStore.loading) return;

      if (!noLoading) {
        leadAanvraagStore.loadingFromNext = true;
      }
      const routeExtensions = useRouteExtensions();

      const leadAanvraag = computed(() => leadAanvraagStore.leadAanvraag);

      let aanbodDefinitie;

      aanbodDefinitie = await useAanbodDefinitieByVersionQueryInFunction(
        queryClient,
        leadAanvraag.value.aanbodVersieId,
        leadAanvraagStore.subflowVersieIds,
        routeExtensions.lang
      );

      await (savingPromise ?? Promise.resolve());
      const stapConfigStore = useStapConfigStore();

      let isFlowOpener = false;
      if (!pageHasMultipleStappen.value) {
        const stapContentComponent = currentPageConfig?.value?.stappen[0]?.contentComponent;
        isFlowOpener =
          stapContentComponent === 'FlowOpener' || stapContentComponent === 'IntroFlowOpener';
      }

      updateCurrentSubflowState(
        subflowState,
        isFlowOpener,
        subflowOverride ?? routeExtensions.subflowNaam.value
      );
      let putResult;
      try {
        putResult = await leadAanvraagApi.putLeadAanvraag(
          {
            aanbod: aanbodOverride ?? routeExtensions.aanbodNaam.value,
            subflow: subflowOverride ?? routeExtensions.subflowNaam.value,
            page: routeExtensions.pageNaam.value,
            lang: routeExtensions.lang.value
          },
          leadAanvraag.value
        );
      } catch (error) {
        if (error.response?.data?.errorMessages || error.response?.data?.notification) {
          leadAanvraagStore.updateStapErrors(error.response.data.errorMessages);
          leadAanvraagStore.setValueOnLeadAanvraag({
            contentLocator: `suppressedErrors`,
            value: error.response.data.suppressedErrors
          });
        } else {
          //reset stepErrorMessages
          leadAanvraagStore.updateStapErrors(undefined);
        }

        if (error.response.data.notification) {
          notificationsStore.openPopUp({
            ...error.response.data.notification,
            content: error.response.data.notification.beschrijving
          });
        }

        if (error.response?.data?.pageErrorMessages) {
          leadAanvraagStore.updatePageErrors(error.response?.data?.pageErrorMessages);
        } else {
          //reset PageErrorMessages
          leadAanvraagStore.updatePageErrors(undefined);
        }
        return;
      }

      if (putResult.headers['next-url']) {
        window.location = putResult.headers['next-url'];
        return putResult.data.leadAanvraag;
      }

      const isSingleFlowAanbod = () =>
        aanbodDefinitie.subflows && aanbodDefinitie.subflows.length == 1;
      let url = flowUrls.overzichtUrl.value;
      if (isSingleFlowAanbod()) {
        url = flowUrls.betaaloverzichtUrl.value;
      }

      if (putResult.headers['next-subflow']) {
        const decode = (headerName) => Base64.decode(putResult.headers[headerName]);

        url = `/${routeExtensions.lang.value}/${
          aanbodOverride ?? routeExtensions.aanbodNaam.value
        }/${decode('next-subflow')}/${decode('next-page')}?aanvraagId=${
          routeExtensions.aanvraagId.value ?? leadAanvraagStore.leadAanvraag.aanvraagId
        }`;

        var data = await leadAanvraagApi.getLeadAanvraag(
          {
            aanbod: aanbodOverride ?? routeExtensions.aanbodNaam.value,
            subflow: decode('next-subflow'),
            page: decode('next-page'),
            lang: routeExtensions.lang.value
          },
          { aanvraagId: leadAanvraagStore.leadAanvraag.aanvraagId }
        );
        //reset errormessages
        leadAanvraagStore.resetAllErrorMessages();
        leadAanvraagStore.updateLeadAanvraag(data.leadAanvraag);

        //TO-IMPLEMENT this store should not exist, this is a part of aanbodDefinitie so should be updated by aanbodDefinitie query
        //Currently not reworked because of pre data hooks in interceptors, see adl for more information
        stapConfigStore.setConfigs(data.page.stappen, data.page.naam);
      } else {
        leadAanvraagStore.updateLeadAanvraag(putResult.data.leadAanvraag);
        // Support to finalize aanbod at any point in the flow

        const pageConfig = currentPageConfig.value;
        if (isSingleFlowAanbod() && pageConfig.metaData['FinalizeOnNext'] === 'true') {
          await flowNavigation.finalize({});
          return;
        }
      }
      await router.push(url);

      return putResult.data.leadAanvraag;
    } finally {
      leadAanvraagStore.loadingFromNext = false;
    }
  }

  return {
    next
  };
};
